import { injectable, inject } from 'inversify-props';
import { types, IState } from '@/store';
import { Store } from 'vuex';
import { mcLogger, UniquePromise } from '@/utils';
import { ICounterApi } from '@/apis';
import { IAuthService } from "@/services";

export interface ISiteService {
    /** Request and populate the store with the information needed to use the app */
    activate(): any;

    /**
     * Update the store with the information needed to run the application,
     *  such as: feature flags; sites; sponsors; programs; and site app dates.
     * @param data The information to be loaded into the store
     */
    loadAppData(data: any);
}

@injectable()
export class SiteService implements ISiteService {
  @inject() private store!: Store<IState>;
  @inject() private counterApi!: ICounterApi;
  @inject() private readonly authService!: IAuthService;
  private readonly console = mcLogger.getConsole(this.constructor.name, mcLogger.colors.SiteService);
  activatePromise = new UniquePromise();

  async activate() {
    return this.activatePromise.run(
      async () => {
        await this.loadSites();
      });
  }

  /**
   * Request the information needed to run the application while offline.
   * If successful, load the response into the store.
   */
  private async loadSites() {
    if (await this.authService.isAuthenticated() && navigator.onLine) {
      const response = await this.counterApi.getOfflineInfo();
      if (response.status === 200) {
        await this.loadAppData(response.data.data);
      }
    }
  }

  async loadAppData(data) {
    await this.store.dispatch(types.actions.setFeatureFlags, data.featureFlagList);
    await this.store.dispatch(types.actions.setSites, data.sites);
    await this.store.dispatch(types.actions.setSponsors, data.sponsors);
    await this.store.dispatch(types.actions.setPrograms, data.programs);
    await this.store.dispatch(types.actions.setSiteAppDates, data.siteAppDates);
    await this.store.dispatch(types.actions.setSiteAppNonCongregateMeals, data.siteAppNonCongregateMeals);
  }
}
